<script>

    import { onMount } from 'svelte'
    import { createEventDispatcher } from 'svelte'
    import SignaturePad from 'signature_pad'
    import Portal from 'svelte-portal'

    const dispatch = createEventDispatcher()

    export let tabindex = 0
    export let value
    export let id = 1
    export let sStyle
    export let sClass
    export let required = false

    let data = []
    let canvas = null
    let signature = null
    let canvasWidth = 0
    let canvasHeight = 0
    let openModal = false

    const getCanvas = () => {
        canvas = new SignaturePad(document.getElementById(`canvas-${id}`))
    }

    onMount(getCanvas)

    const sign = () => {

        if( !canvas.isEmpty() ) {

            data = canvas.toData()
            signature = canvas.toDataURL()

            dispatch('sign', { signature: signature })

            openModal = false

        }
        
    }

    const clear = () => {
        canvas.clear()
    }

    const preventTouch = (e) => {
        e.preventDefault()
        e.stopImmediatePropagation()
    }

    $: if ( openModal ) {
    
        window.addEventListener('touchmove', preventTouch, { passive: false })

    } else {

        window.removeEventListener('touchmove', preventTouch, {passive: false })

    }

</script>

<style>

    .sign-modal-head {
        @apply px-2 absolute grid grid-cols-sign-input items-center border-2 border-solid border-ch-blue rounded cursor-pointer;
    }

    .sign-modal-head-value {
        @apply w-full h-full relative;
    }

    .sign-modal-signature {
        @apply absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2;
    }

    .sign-modal-head-placeholder {
        @apply text-sm flex-grow outline-none select-none;
    }

    .sign-modal-head-icon {
        @apply flex items-center justify-center;
    }

    .sign-modal-body {

        @apply w-full h-full overflow-hidden cursor-default transition-opacity duration-500 bg-ch-blue bg-opacity-50 opacity-0 pointer-events-none fixed top-0 z-20 left-0 rounded-lg shadow-xl;
    }

    .sign-modal-body-content {
        @apply w-6/12 h-104 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col py-0 px-6 bg-gray-200 rounded-lg shadow-xl;
    }

    .open-modal {
        @apply opacity-100 pointer-events-auto;
    }

    .sign-modal-body-title {
        @apply py-4 flex items-center justify-between;
    }

    .sign-modal-body-title-button {
        @apply w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center cursor-pointer;
    }

    .signature-canvas {
        @apply flex-grow relative rounded-lg overflow-hidden;
    }

    .canvas-layout {
        width: 90%;
        height: 1px;

        position: absolute;
        left: 50%;
        top: 70%;

        transform: translateX( -50% );

        background-color: black;
    }

    .canvas-layout-icon {

        position: absolute;
        right: 0px;
        top: 50%;

        transform: translateY( -50% );
    }

    .sign-module-body-foot {
        @apply flex items-center py-4;
    }

    .sign-module-date {
        @apply text-sm flex-grow;
    }

    .h-space-4 {
        margin-left: 20px;
    }

    .required {
        @apply border-red-500 bg-red-200;
    }

@media ( max-width: 768px ) {

    .sign-modal-head-placeholder {
        @apply text-xxs;
    }

    .sign-modal-head-icon {
        @apply hidden;
    }

    .sign-modal-body {
        @apply z-50 rounded-none;
    }

    .sign-modal-body-content {
        height: 80%;
        top: 10%;
        @apply w-full absolute left-0 transform translate-x-0 translate-y-0 flex flex-col py-0 px-4 bg-gray-200 rounded-lg shadow-xl;
    }

    .sign-modal-body-title {
        @apply py-4;
    }

    .canvas-layout {

        top: 60%;
    }

}

</style>

<div 

id={id}
value={value}
tabindex={tabindex}
style={`${sStyle}`} 
class={`sign-modal-head ${sClass}`} 
class:required={required}
on:click={() => openModal = true }
on:focus={() => openModal = true }

>

    { #if value }

        <div class="sign-modal-head-value" >

            <img src={value} alt="Signature" class="sign-modal-signature" >

        </div>

    { :else }

        <p class="sign-modal-head-placeholder" >Sign</p>

    { /if }

    <div class="sign-modal-head-icon" >

        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 9.4V13H4.6L13 4.6L9.4 1L1 9.4Z" stroke="#082E45" stroke-width="2" stroke-linejoin="round"/>
        </svg>
            
    </div>

    <!-- Sign Modal -->

    <Portal target="#modal-portal" >

    <div 

    class="sign-modal-body" 
    class:open-modal={openModal} 

    >

        <div class="sign-modal-body-content" >

            <div class="sign-modal-body-title" >

                <p>Sign Document</p>

                <div class="sign-modal-body-title-button" on:click|stopPropagation="{ () => openModal = false }" >

                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.521168 1.87906C0.146264 1.50415 0.146264 0.896315 0.521168 0.521412C0.896071 0.146509 1.50391 0.146509 1.87881 0.521412L5.99999 4.64259L10.1212 0.521412C10.4961 0.146509 11.1039 0.146509 11.4788 0.521412C11.8537 0.896315 11.8537 1.50415 11.4788 1.87906L7.35764 6.00023L11.4788 10.1214C11.8537 10.4963 11.8537 11.1042 11.4788 11.4791C11.1039 11.854 10.4961 11.854 10.1212 11.4791L5.99999 7.35788L1.87881 11.4791C1.50391 11.854 0.896071 11.854 0.521168 11.4791C0.146265 11.1042 0.146265 10.4963 0.521168 10.1214L4.64235 6.00023L0.521168 1.87906Z" fill="white"/>
                    </svg>

                </div>

            </div>

            <div 
            
            bind:clientWidth={canvasWidth} 
            bind:clientHeight={canvasHeight} 
            class="signature-canvas" 
            
            >

                <div class="canvas-layout" >

                    <svg class="canvas-layout-icon" width="37" height="20" viewBox="0 0 37 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.54743 10.3347L9.36984 10.104C9.38127 10.1149 9.39067 10.1258 9.39948 10.136C9.41554 10.1547 9.42964 10.171 9.45047 10.1809C9.4021 10.2424 9.3497 10.3078 9.29729 10.3731C9.24488 10.4385 9.19246 10.5038 9.14408 10.5654C8.86351 10.9208 8.58076 11.2741 8.29837 11.627C7.49337 12.6329 6.69134 13.6351 5.95118 14.6711C5.09652 15.8859 4.32248 17.1468 3.56457 18.4078C3.22593 18.9767 3.50007 19.5764 4.11285 19.8532C4.79013 20.1608 5.32228 19.9916 5.74155 19.2843C7.95078 15.5168 10.6599 12.1185 13.756 9.02763C14.3366 8.45868 14.9332 7.93585 15.7234 7.65906C15.9229 7.59248 16.1039 7.49648 16.2854 7.40018C16.3974 7.34079 16.5096 7.28129 16.6264 7.2285C19.4484 5.85992 22.0931 4.21456 24.4152 2.154C24.6087 1.98485 24.7861 1.80033 24.8828 1.58505C25.2215 0.816183 24.6087 0.0165659 23.6895 0.00118863C22.1414 -0.0295659 20.803 0.539392 19.5774 1.36976C17.5133 2.78447 15.5138 4.29144 13.6432 5.96756C13.4819 6.12133 13.2561 6.25973 13.0465 6.33662C12.6918 6.47014 12.3371 6.60881 11.9816 6.7478C11.1088 7.08899 10.2313 7.43203 9.33759 7.70519C7.62296 8.2393 5.89213 8.73478 4.16233 9.22997C3.45185 9.43335 2.74156 9.63669 2.03263 9.84263C1.62948 9.95027 1.27472 10.1194 1.09733 10.5346C0.903825 10.9959 1.00058 11.4265 1.32309 11.7955C1.69399 12.2107 2.17776 12.1646 2.66153 12.0262C4.25713 11.5772 5.84211 11.1181 7.4337 10.6571C7.80451 10.5497 8.17568 10.4422 8.54743 10.3347ZM21.3619 9.78342C21.1485 9.82557 20.9346 9.86784 20.7224 9.91843C20.7063 9.89536 20.6942 9.8723 20.6821 9.84923C20.67 9.82616 20.6579 9.8031 20.6418 9.78003C20.6821 9.70315 20.7234 9.62626 20.7647 9.54938C20.8887 9.31872 21.0126 9.08806 21.1094 8.8574C21.319 8.36532 21.1255 7.95014 20.7708 7.59646C20.4321 7.24278 20.029 7.18128 19.5613 7.3658C19.296 7.47349 19.0266 7.5774 18.7568 7.68151C18.256 7.87472 17.7535 8.06859 17.2715 8.28844C15.9492 8.90353 14.6269 9.534 13.3207 10.1798C12.7401 10.4566 12.5305 10.8872 12.6434 11.3946C12.8369 12.2404 13.6109 12.5633 14.4656 12.1481C14.8687 11.9482 15.2719 11.7445 15.675 11.5407C16.0782 11.337 16.4813 11.1332 16.8845 10.9333C17.1046 10.8167 17.3432 10.7266 17.6004 10.6295C17.6824 10.5986 17.7663 10.567 17.852 10.5335V10.9641C17.8681 11.7791 18.2874 12.1789 19.1421 12.1943C21.2707 12.2558 23.2703 11.8252 25.1408 10.8564C25.286 10.7796 25.544 10.7488 25.6891 10.8103C26.3503 11.0871 27.0276 11.1025 27.7048 10.9487C27.9359 10.8973 28.167 10.8354 28.3998 10.773C28.6544 10.7047 28.9111 10.6359 29.1723 10.5796C29.3013 10.5489 29.527 10.5643 29.5593 10.6258C29.9302 11.3024 30.5591 11.3793 31.2202 11.3331C32.8006 11.2101 34.3809 11.0563 35.9451 10.8718C36.6224 10.7796 37.1061 10.0414 36.9771 9.34947C36.8643 8.74976 36.4289 8.48834 35.6387 8.59598C35.4635 8.62307 35.287 8.65149 35.1094 8.68006C34.6822 8.7488 34.2494 8.81846 33.8165 8.87277C33.3463 8.93534 32.8687 8.98376 32.3887 9.03242C32.1608 9.05552 31.9324 9.07868 31.704 9.10343C31.6806 9.01404 31.6647 8.94269 31.6509 8.88064C31.6362 8.81461 31.6239 8.75912 31.6073 8.70362C31.3976 7.96552 30.7203 7.64259 29.9624 7.88863C29.0594 8.1808 28.1564 8.45759 27.2372 8.70362C27.0598 8.74976 26.7857 8.70362 26.6567 8.59598C25.9471 7.95014 25.6246 7.93476 24.7861 8.41146C24.0927 8.81126 23.3831 9.18032 22.6413 9.45711C22.2302 9.61189 21.7972 9.69743 21.3619 9.78342Z" fill="#082E45"/>
                    </svg>

                </div>

                <canvas id="canvas-{id}" style="background-color: white;" width={canvasWidth} height={canvasHeight} ></canvas>

            </div>

            <div class="sign-module-body-foot" >

                <p class="sign-module-date" >{ 
                    new Date().getDate() + ' ' + 
                    new Date().toLocaleString('default', { month: 'long' }) + ', ' + 
                    new Date().getFullYear() 
                }</p>

                <div class="b-regular" on:click={clear} >Clear</div>
                <div class="b-main h-space-4" on:click|stopPropagation={sign} >Sign</div>

            </div>

        </div>

    </div>

    </Portal>

</div>

